/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  McpEnvironmentConfig,
  McpEnvironmentConfig$inboundSchema,
} from "./mcpenvironmentconfig.js";

/**
 * Metadata used to configure the MCP install page. toolset_id or mcp_server_id identifies which backend the metadata belongs to; a hosted MCP server with a live canonical MCP server carries both, set to the same id.
 */
export type McpMetadata = {
  /**
   * When the metadata entry was created
   */
  createdAt: Date;
  /**
   * The default environment to load variables from
   */
  defaultEnvironmentId?: string | undefined;
  /**
   * The list of environment variables configured for this MCP
   */
  environmentConfigs?: Array<McpEnvironmentConfig> | undefined;
  /**
   * A blob of text for the button on the MCP server page
   */
  externalDocumentationText?: string | undefined;
  /**
   * A link to external documentation for the MCP install page
   */
  externalDocumentationUrl?: string | undefined;
  /**
   * The ID of the metadata record
   */
  id: string;
  /**
   * URL to redirect to instead of showing the default installation page
   */
  installationOverrideUrl?: string | undefined;
  /**
   * Server instructions returned in the MCP initialize response
   */
  instructions?: string | undefined;
  /**
   * The asset ID for the MCP install page logo
   */
  logoAssetId?: string | undefined;
  /**
   * The MCP server associated with this install page metadata. Also set with toolset_id, to the same id, for a hosted MCP server with a live canonical MCP server.
   */
  mcpServerId?: string | undefined;
  /**
   * The toolset associated with this install page metadata. Also set with mcp_server_id, to the same id, for a hosted MCP server with a live canonical MCP server.
   */
  toolsetId?: string | undefined;
  /**
   * When the metadata entry was last updated
   */
  updatedAt: Date;
};

/** @internal */
export const McpMetadata$inboundSchema: z.ZodMiniType<McpMetadata, unknown> = z
  .pipe(
    z.object({
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      default_environment_id: z.optional(z.string()),
      environment_configs: z.optional(
        z.array(McpEnvironmentConfig$inboundSchema),
      ),
      external_documentation_text: z.optional(z.string()),
      external_documentation_url: z.optional(z.string()),
      id: z.string(),
      installation_override_url: z.optional(z.string()),
      instructions: z.optional(z.string()),
      logo_asset_id: z.optional(z.string()),
      mcp_server_id: z.optional(z.string()),
      toolset_id: z.optional(z.string()),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "default_environment_id": "defaultEnvironmentId",
        "environment_configs": "environmentConfigs",
        "external_documentation_text": "externalDocumentationText",
        "external_documentation_url": "externalDocumentationUrl",
        "installation_override_url": "installationOverrideUrl",
        "logo_asset_id": "logoAssetId",
        "mcp_server_id": "mcpServerId",
        "toolset_id": "toolsetId",
        "updated_at": "updatedAt",
      });
    }),
  );

export function mcpMetadataFromJSON(
  jsonString: string,
): SafeParseResult<McpMetadata, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => McpMetadata$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'McpMetadata' from JSON`,
  );
}
